import { useState, useEffect, useRef } from 'react';
import { getAppsGatewayUrl } from '../../../../../../../constants/env';

const WORKFLOW_ID = 'thanks.offers';

interface UseThanksConfigParams {
  retailerMoniker: string;
  orderNumber?: string;
  trackingNumber?: string;
}

interface ThanksItem {
  name: string;
  category: string;
  value: number;
  currency: string;
  quantity: number;
}

interface ThanksLocation {
  country?: string;
}

interface UseThanksConfigResult {
  partnerId: string | null;
  emailHash: string | null;
  items?: ThanksItem[];
  purchaseLocation?: ThanksLocation;
  customerLocation?: ThanksLocation;
  categories?: string[];
  isLoading: boolean;
}

interface ThanksApiResponse {
  partnerId: string;
  emailHash: string | null;
  items?: ThanksItem[];
  purchaseLocation?: ThanksLocation;
  customerLocation?: ThanksLocation;
  categories?: string[];
}

export function useThanksConfig({
  retailerMoniker,
  orderNumber,
  trackingNumber,
}: UseThanksConfigParams): UseThanksConfigResult {
  const hasInitialized = useRef(false);
  const [result, setResult] = useState<
    Omit<UseThanksConfigResult, 'isLoading'>
  >({
    partnerId: null,
    emailHash: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (hasInitialized.current || !retailerMoniker) {
      setIsLoading(false);
      return;
    }

    hasInitialized.current = true;

    (async () => {
      try {
        const appsGatewayUrl = getAppsGatewayUrl();

        const res = await fetch(`${appsGatewayUrl}/invoke`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workflowId: WORKFLOW_ID,
            context: {
              retailerMoniker,
            },
            body: {
              orderNumber,
              trackingNumber,
            },
          }),
        });

        if (!res.ok) {
          throw new Error(`Thanks API error: ${res.status}`);
        }

        const { data } = (await res.json()) as { data: ThanksApiResponse };

        setResult({
          partnerId: data.partnerId,
          emailHash: data.emailHash,
          items: data.items,
          purchaseLocation: data.purchaseLocation,
          customerLocation: data.customerLocation,
          categories: data.categories,
        });
      } catch (err) {
        console.error('Thanks config fetch failed', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [retailerMoniker, orderNumber, trackingNumber]);

  return {
    ...result,
    isLoading,
  };
}
